// components/GlobalStyles.jsx
// Injects all global CSS once at module root level
export default function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Outfit:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap');

      *, *::before, *::after { box-sizing: border-box; }

      .dp-module {
        font-family: 'Outfit', sans-serif;
        font-size: 14px;
        color: #1e293b;
        background: #f1f5f9;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      .dp-module * { font-family: inherit; }

      .dp-module h1, .dp-module h2, .dp-module h3,
      .dp-module h4, .dp-module h5 {
        font-family: 'Playfair Display', serif;
        font-weight: 700;
        color: #0f172a;
        line-height: 1.25;
      }

      .dp-mono { font-family: 'DM Mono', monospace !important; }

      /* Scrollbar */
      .dp-module ::-webkit-scrollbar { width: 5px; height: 5px; }
      .dp-module ::-webkit-scrollbar-track { background: #f1f5f9; }
      .dp-module ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }

      /* Animations */
      @keyframes dpSlideIn {
        from { opacity: 0; transform: translateX(20px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes dpFadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes dpSpin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      .dp-fade-in { animation: dpFadeIn 0.2s ease; }
      .dp-spin { animation: dpSpin 1s linear infinite; display: inline-block; }

      /* Nav hover */
      .dp-nav-btn:hover {
        color: #2563eb !important;
        background: #f0f7ff !important;
      }

      /* Table row hover */
      .dp-row-hover:hover {
        background: #f8fafc !important;
        cursor: pointer;
      }
      .dp-row-hover:hover td { color: #0f172a; }

      /* Card hover for clickable cards */
      .dp-card-click:hover {
        box-shadow: 0 4px 20px rgba(37,99,235,0.12) !important;
        border-color: #bfdbfe !important;
        transform: translateY(-1px);
        transition: all 0.15s ease;
      }

      /* Button base */
      .dp-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 18px;
        border-radius: 8px; border: none;
        font-family: 'Outfit', sans-serif;
        font-size: 13px; font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
      }
      .dp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .dp-btn-primary { background: #2563eb; color: #fff; box-shadow: 0 2px 8px rgba(37,99,235,0.3); }
      .dp-btn-primary:hover:not(:disabled) { background: #1d4ed8; box-shadow: 0 4px 12px rgba(37,99,235,0.4); }
      .dp-btn-outline { background: #fff; color: #2563eb; border: 1.5px solid #bfdbfe; }
      .dp-btn-outline:hover:not(:disabled) { background: #eff6ff; border-color: #93c5fd; }
      .dp-btn-success { background: #16a34a; color: #fff; box-shadow: 0 2px 8px rgba(22,163,74,0.3); }
      .dp-btn-success:hover:not(:disabled) { background: #15803d; }
      .dp-btn-danger  { background: #fef2f2; color: #b91c1c; border: 1px solid #fca5a5; }
      .dp-btn-danger:hover:not(:disabled)  { background: #fee2e2; }
      .dp-btn-ghost   { background: transparent; color: #64748b; border: 1px solid #e2e8f0; }
      .dp-btn-ghost:hover:not(:disabled)   { background: #f8fafc; color: #1e293b; }
      .dp-btn-sm { padding: 5px 12px; font-size: 12px; border-radius: 6px; }

      /* Form inputs */
      .dp-input {
        width: 100%; padding: 10px 14px;
        border: 1.5px solid #e2e8f0; border-radius: 8px;
        font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 500;
        color: #1e293b; background: #fff;
        outline: none; transition: border-color 0.15s;
      }
      .dp-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
      .dp-input::placeholder { color: #94a3b8; font-weight: 400; }

      .dp-select {
        width: 100%; padding: 10px 14px;
        border: 1.5px solid #e2e8f0; border-radius: 8px;
        font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 500;
        color: #1e293b; background: #fff;
        outline: none; cursor: pointer;
        transition: border-color 0.15s;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2394a3b8' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 36px;
      }
      .dp-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }

      .dp-label {
        display: block;
        font-size: 12px; font-weight: 700;
        color: #374151; letter-spacing: 0.3px;
        margin-bottom: 7px;
        text-transform: uppercase;
      }

      /* Card */
      .dp-card {
        background: #fff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.05);
      }

      /* Table */
      .dp-table-header {
        display: grid;
        padding: 10px 20px;
        background: #f8fafc;
        border-bottom: 2px solid #e2e8f0;
      }
      .dp-table-header span {
        font-size: 11px; font-weight: 800;
        color: #64748b;
        text-transform: uppercase; letter-spacing: 0.8px;
      }
      .dp-table-row {
        display: grid;
        padding: 14px 20px;
        align-items: center;
        border-bottom: 1px solid #f1f5f9;
        transition: background 0.1s;
      }
      .dp-table-row:last-child { border-bottom: none; }
      .dp-table-row:hover { background: #f8fafc; }

      /* Divider */
      .dp-divider { width: 1px; background: #e2e8f0; align-self: stretch; }

      /* Filter bar */
      .dp-filter-btn {
        padding: 5px 14px;
        border-radius: 7px; border: 1.5px solid transparent;
        font-family: 'Outfit', sans-serif;
        font-size: 12px; font-weight: 600;
        cursor: pointer; transition: all 0.12s;
        background: transparent; color: #94a3b8;
      }
      .dp-filter-btn:hover { color: #475569; background: #f8fafc; }
      .dp-filter-btn.active { border-color: currentColor; }

      /* Status strip on table row */
      .dp-row-selected { border-left: 3px solid #2563eb !important; background: #f0f7ff !important; }

      /* Loading spinner */
      .dp-loader {
        width: 18px; height: 18px;
        border: 2px solid #e2e8f0;
        border-top-color: #2563eb;
        border-radius: 50%;
        animation: dpSpin 0.7s linear infinite;
        display: inline-block;
      }
    `}</style>
  );
}
